import { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { getFingerprintForHeader } from '../services/clientAuth';
import { hasActiveTextSelection } from '../utils/textSelection';

function buildReleaseUrl(taskId) {
    const base = String(api.defaults.baseURL || '').replace(/\/$/, '');
    const path = `/api/tasks/${taskId}/presence`;
    return base ? `${base}${path}` : path;
}

function buildViewersSignature(viewers) {
    return (Array.isArray(viewers) ? viewers : [])
        .map((viewer) => [
            viewer?.userId ?? '',
            viewer?.name ?? '',
            viewer?.state ?? '',
        ].join(':'))
        .sort()
        .join('|');
}

export default function useTaskPresence(taskId) {
    const [activeViewers, setActiveViewers] = useState([]);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (!taskId) {
            setActiveViewers([]);
            return () => {};
        }

        let cancelled = false;

        const clearHeartbeat = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };

        const applyResponse = (response) => {
            if (cancelled) {
                return;
            }

            if (hasActiveTextSelection()) {
                return;
            }

            const viewers = Array.isArray(response?.data?.activeViewers) ? response.data.activeViewers : [];
            setActiveViewers((prevViewers) => (
                buildViewersSignature(prevViewers) === buildViewersSignature(viewers)
                    ? prevViewers
                    : viewers
            ));
        };

        const sendHeartbeat = async (visibility) => {
            try {
                const response = await api.post(`/api/tasks/${taskId}/presence/heartbeat`, {
                    visibility,
                });
                applyResponse(response);
            } catch (error) {
                if (!cancelled) {
                    console.error('Не удалось обновить presence тикета:', error);
                }
            }
        };

        const sendRelease = () => {
            const fingerprint = getFingerprintForHeader();
            const headers = fingerprint ? { 'X-CSRF-Token': fingerprint } : {};

            try {
                window.fetch(buildReleaseUrl(taskId), {
                    method: 'DELETE',
                    credentials: 'include',
                    keepalive: true,
                    headers,
                }).catch(() => {});
            } catch {
                // ignore keepalive errors
            }
        };

        const startVisibleLoop = () => {
            clearHeartbeat();
            void sendHeartbeat('visible');
            intervalRef.current = setInterval(() => {
                if (document.visibilityState !== 'visible') {
                    return;
                }
                void sendHeartbeat('visible');
            }, 2000);
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                clearHeartbeat();
                void sendHeartbeat('hidden');
                return;
            }

            startVisibleLoop();
        };

        const handleBeforeUnload = () => {
            sendRelease();
        };

        startVisibleLoop();
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            cancelled = true;
            clearHeartbeat();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            sendRelease();
        };
    }, [taskId]);

    return { activeViewers };
}
